import { claudeText, claudeJson, claudeVision, claudeResearch, ClaudeResearchResult } from './claudeProvider';
import { blobToBase64 } from './blob';
import { geminiText, geminiImage } from './geminiProvider';
import { pollinationsText } from './pollinationsProvider';
import { getKey } from './keys';
import { AIError, AIProvider, AITextResult, describeAIError } from './types';

export { AIError, describeAIError, shouldAutoRetry } from './types';
export type { AIProvider, AITextResult } from './types';
export type { ClaudeResearchResult } from './claudeProvider';

export interface TextProviders {
  claude: (prompt: string, key: string) => Promise<string>;
  gemini: (prompt: string, key: string) => Promise<string>;
  pollinations: (prompt: string) => Promise<string>;
}

const defaultProviders: TextProviders = {
  claude: (prompt, key) => claudeText(prompt, key),
  gemini: geminiText,
  pollinations: prompt => pollinationsText(prompt),
};

// A blank answer must never reach a caller: it would overwrite cached lists and form fields.
const nonEmpty = (text: string, provider: AIProvider): AITextResult => {
  if (!text || !text.trim()) {
    throw new AIError(provider, 'The AI returned an empty answer. Retry.', true);
  }
  return { text, provider };
};

export const selectAndGenerate = async (
  prompt: string,
  keys: { claude: string; gemini: string },
  providers: TextProviders
): Promise<AITextResult> => {
  if (keys.claude) {
    return nonEmpty(await providers.claude(prompt, keys.claude), 'claude');
  }

  if (keys.gemini) {
    try {
      return nonEmpty(await providers.gemini(prompt, keys.gemini), 'gemini');
    } catch (err) {
      console.warn('Gemini failed, falling back to Pollinations.', err);
    }
  }

  try {
    return nonEmpty(await providers.pollinations(prompt), 'pollinations');
  } catch (err) {
    throw err instanceof AIError ? err : new AIError('pollinations', describeAIError(err), true);
  }
};

export const generateAIResult = (prompt: string): Promise<AITextResult> =>
  selectAndGenerate(
    prompt,
    { claude: getKey('claudeToken'), gemini: getKey('geminiToken') },
    defaultProviders
  );

// Web research has no free-engine equivalent, so it requires a Claude key rather than falling back.
export const researchWithClaude = async (
  prompt: string,
  claudeKey: string = getKey('claudeToken'),
  search: (prompt: string, key: string) => Promise<string> = (p, k) => claudeText(p, k, undefined, { webSearch: true })
): Promise<AITextResult> => {
  if (!claudeKey) {
    throw new AIError('claude', 'Song prompts need a Claude API key. Add one in Settings > API Keys.');
  }
  return nonEmpty(await search(prompt, claudeKey), 'claude');
};

const STUDIO_NEEDS_KEY = 'The Level Studio needs a Claude API key. Add one in Settings > API Keys.';

// Structured calls are Studio-only and have no free-engine equivalent.
export const claudeJsonWithSavedKey = async (
  prompt: string,
  schema: Record<string, unknown>,
  claudeKey: string = getKey('claudeToken'),
  run: (prompt: string, schema: Record<string, unknown>, key: string) => Promise<unknown> = (p, s, k) => claudeJson(p, s, k)
): Promise<unknown> => {
  if (!claudeKey) {
    throw new AIError('claude', STUDIO_NEEDS_KEY);
  }
  return run(prompt, schema, claudeKey);
};

// Plain Claude text for the Studio: no fallback to another engine.
export const claudeTextWithSavedKey = async (
  prompt: string,
  claudeKey: string = getKey('claudeToken'),
  run: (prompt: string, key: string) => Promise<string> = (p, k) => claudeText(p, k)
): Promise<string> => {
  if (!claudeKey) throw new AIError('claude', STUDIO_NEEDS_KEY);
  return nonEmpty(await run(prompt, claudeKey), 'claude').text;
};

// Claude with web search for the Studio, returning the answer and the pages the search found.
export const researchSourcesWithSavedKey = async (
  prompt: string,
  claudeKey: string = getKey('claudeToken'),
  run: (prompt: string, key: string) => Promise<ClaudeResearchResult> = (p, k) => claudeResearch(p, k)
): Promise<ClaudeResearchResult> => {
  if (!claudeKey) throw new AIError('claude', STUDIO_NEEDS_KEY);
  const result = await run(prompt, claudeKey);
  nonEmpty(result.text, 'claude');
  return result;
};

// Claude looks at a picture and answers in the given JSON shape.
export const claudeVisionWithSavedKey = async (
  image: Blob,
  prompt: string,
  schema: Record<string, unknown>,
  claudeKey: string = getKey('claudeToken'),
  run: (image: Blob, prompt: string, schema: Record<string, unknown>, key: string) => Promise<unknown> =
    async (img, p, s, k) => claudeVision({ mediaType: img.type || 'image/jpeg', data: await blobToBase64(img) }, p, s, k)
): Promise<unknown> => {
  if (!claudeKey) throw new AIError('claude', STUDIO_NEEDS_KEY);
  return run(image, prompt, schema, claudeKey);
};

export const generateImage = async (prompt: string): Promise<Blob> => {
  const key = getKey('geminiToken');
  if (!key) {
    throw new AIError('gemini', 'Add a Gemini API key in Settings > API Keys to generate images.');
  }
  return geminiImage(prompt, key);
};
